'use client';

import { useMemo, useState } from 'react';
import SettingsForm from 'src/components/Settings/SettingsForm';
import DevMobileForm from '../DevMobileForm';
import { useUpdateEffect } from 'react-use';
import { TabOption } from 'src/components/primitives/Tabs/types';
import useSettingsModalDevForm from 'src/components/Modals/SettingsModal/hooks/useSettingsModalDevForm';
import { SettingsModalView } from 'src/components/Modals/SettingsModal/types';
import useModal from 'src/components/Modals/hooks/useModal';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';

export interface SettingsModalProps {
	/**
	 * If provided, will open to the settings view corresponding to the index.
	 *
	 * Note, the available views differ between mobile and desktop. Ensure
	 * the value you pass in accounts for this. If the value isn't found,
	 * the first logical view will be displayed.
	 */
	view?: SettingsModalView;
}

const VIEW_OPTIONS: TabOption<SettingsModalView>[] = [
	{
		value: 'trade',
		label: 'Trade',
		screenSize: 'all',
	},
	{
		value: 'network',
		label: 'Network',
		screenSize: 'all',
	},
	{
		value: 'margin',
		label: 'Margin',
		screenSize: 'all',
	},
	{
		value: 'layout',
		label: 'Layout',
		screenSize: 'desktop',
	},
	{
		value: 'interface',
		label: 'Interface',
		screenSize: 'all',
	},
	{
		value: 'other',
		label: 'Other',
		screenSize: 'all',
	},
];

const SettingsModal = ({ view: viewFromProps }: SettingsModalProps) => {
	const { hide, goBack, canGoBack } = useModal();
	const isConnected = useWalletIsConnected();
	const { handleSettingsClick, showMobileDevForm, closeDevForm } =
		useSettingsModalDevForm();

	const viewOptions = useMemo(() => {
		return VIEW_OPTIONS.filter((option) => {
			// Exclude margin settings if the user is not connected.
			return option.value !== 'margin' || isConnected;
		});
	}, [isConnected]);

	const view = useMemo(() => {
		const views = viewOptions.map((option) => option.value);

		if (!viewFromProps || !views.includes(viewFromProps)) {
			return views[0];
		}

		return viewFromProps;
	}, [viewFromProps, viewOptions]);

	const [currentView, setCurrentView] = useState(view);

	// Very edge-case-y, but this recalculates the current view if the user
	// switches between mobile and desktop screen sizes.
	useUpdateEffect(() => {
		setCurrentView(view);
	}, [view]);

	return (
		<BaseModal.Container
			onClose={hide}
			className="w-[min(100%,600px)] sm:h-[650px]"
			dataTestId="settings-modal"
		>
			<BaseModal.Title
				onClose={hide}
				tabs={viewOptions}
				activeTab={currentView}
				onTabChange={setCurrentView}
				closeButtonTestId="settings-modal-close"
			>
				<span onClick={handleSettingsClick}>Settings</span>
			</BaseModal.Title>
			{showMobileDevForm ? (
				<DevMobileForm onClose={closeDevForm} />
			) : (
				<SettingsForm
					onClose={canGoBack ? goBack : hide}
					currentView={currentView}
				/>
			)}
		</BaseModal.Container>
	);
};

export default SettingsModal;
